<template>
    <div class="form-group">
        <label v-if="label" class="form-field-label">
            {{ label }}<span v-if="required" class="form-field-required">*</span>
        </label>

        <!-- Custom dropdown -->
        <div v-if="type === 'select'" class="form-field-input" :class="{ open: dropdownOpen, 'form-field-input--error': error }" @click.stop="dropdownOpen = !dropdownOpen">
            <span class="form-field-value">{{ selectedLabel }}</span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" :style="{ transform: dropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }">
                <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" style="color: var(--text-dim)"/>
            </svg>
            <div v-if="dropdownOpen" class="form-field-dropdown">
                <div
                    v-for="opt in options"
                    :key="opt.value"
                    class="form-field-option"
                    :class="{ selected: value === opt.value }"
                    @click.stop="select(opt)"
                >{{ opt.label }}</div>
            </div>
        </div>

        <!-- Date picker -->
        <div v-else-if="type === 'date'" class="form-field-input" @click.stop="dateOpen = !dateOpen">
            <span class="form-field-value" :class="{ 'form-field-placeholder': !value }">
                {{ value || 'Select date' }}
            </span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" flex-shrink="0">
                <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5" style="color: var(--text-dim)"/>
                <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style="color: var(--text-dim)"/>
            </svg>
            <div v-if="dateOpen" class="date-picker" @click.stop>
                <!-- Year select view -->
                <div v-if="pickerView === 'year'" class="date-ym-grid">
                    <div class="date-ym-header">
                        <button class="date-nav-btn" @pointerdown.prevent="startRepeat('prevYearPage')" @pointerup="stopRepeat" @pointerleave="stopRepeat" @pointercancel="stopRepeat">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style="color: var(--text-sub)"/></svg>
                        </button>
                        <span class="date-month-label">{{ yearStart }} - {{ yearStart + 11 }}</span>
                        <button class="date-nav-btn" @pointerdown.prevent="startRepeat('nextYearPage')" @pointerup="stopRepeat" @pointerleave="stopRepeat" @pointercancel="stopRepeat">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style="color: var(--text-sub)"/></svg>
                        </button>
                    </div>
                    <div class="date-month-grid">
                        <span
                            v-for="i in 12"
                            :key="i"
                            class="date-month-cell"
                            :class="{ 'date-cell--selected': calYear === yearStart + i - 1 }"
                            @click.stop="calYear = yearStart + i - 1; pickerView = 'month'"
                        >{{ yearStart + i - 1 }}</span>
                    </div>
                </div>

                <!-- Month select view -->
                <div v-else-if="pickerView === 'month'" class="date-ym-grid">
                    <div class="date-ym-header">
                        <button class="date-nav-btn" @pointerdown.prevent="startRepeat('prevYear')" @pointerup="stopRepeat" @pointerleave="stopRepeat" @pointercancel="stopRepeat">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style="color: var(--text-sub)"/></svg>
                        </button>
                        <span class="date-month-label date-month-clickable" @click.stop="openYearView">
                            {{ calYear }}
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style="margin-left:2px"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" style="color: var(--icon-muted)"/></svg>
                        </span>
                        <button class="date-nav-btn" @pointerdown.prevent="startRepeat('nextYear')" @pointerup="stopRepeat" @pointerleave="stopRepeat" @pointercancel="stopRepeat">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style="color: var(--text-sub)"/></svg>
                        </button>
                    </div>
                    <div class="date-month-grid">
                        <span
                            v-for="(m, i) in months"
                            :key="i"
                            class="date-month-cell"
                            :class="{ 'date-cell--selected': calMonth === i }"
                            @click.stop="calMonth = i; pickerView = 'day'"
                        >{{ m }}</span>
                    </div>
                </div>

                <!-- Day view -->
                <template v-else>
                    <div class="date-picker-header">
                        <button class="date-nav-btn" @pointerdown.prevent="startRepeat('prevMonth')" @pointerup="stopRepeat" @pointerleave="stopRepeat" @pointercancel="stopRepeat">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style="color: var(--text-sub)"/></svg>
                        </button>
                        <span class="date-month-label date-month-clickable" @click.stop="pickerView = 'month'">
                            {{ monthLabel }}
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style="margin-left:2px"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" style="color: var(--icon-muted)"/></svg>
                        </span>
                        <button class="date-nav-btn" @pointerdown.prevent="startRepeat('nextMonth')" @pointerup="stopRepeat" @pointerleave="stopRepeat" @pointercancel="stopRepeat">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style="color: var(--text-sub)"/></svg>
                        </button>
                    </div>
                    <div class="date-weekdays">
                        <span v-for="d in weekdays" :key="d">{{ d }}</span>
                    </div>
                    <div class="date-grid">
                        <span
                            v-for="(day, i) in calendarDays"
                            :key="i"
                            class="date-cell"
                            :class="{
                                'date-cell--empty': !day,
                                'date-cell--selected': day && isSelected(day),
                                'date-cell--today': day && isToday(day)
                            }"
                            @click.stop="day && selectDate(day)"
                        >{{ day || '' }}</span>
                    </div>
                </template>
            </div>
        </div>

        <!-- Text / number input -->
        <div v-else-if="type === 'text' || type === 'number'" class="form-field-input" :class="{ 'form-field-input--error': error, 'form-field-input--disabled': disabled }">
            <input
                :type="integer ? 'text' : type"
                :inputmode="integer ? 'numeric' : null"
                :value="value"
                :placeholder="placeholder"
                :disabled="disabled"
                class="form-field-native"
                @keydown="onIntegerKeydown"
                @input="onNativeInput"
            />
        </div>

        <!-- Password input -->
        <div v-else-if="type === 'password'" class="form-field-input" :class="{ 'form-field-input--error': error }">
            <input
                :type="showPassword ? 'text' : 'password'"
                :value="value"
                :placeholder="placeholder"
                class="form-field-native"
                @input="$emit('input', $event.target.value)"
            />
            <button class="form-field-eye" type="button" @click="showPassword = !showPassword">
                <svg v-if="!showPassword" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 3C4.667 3 1.82 5.073 1 8c.82 2.927 3.667 5 7 5s6.18-2.073 7-5c-.82-2.927-3.667-5-7-5zm0 8.333A3.333 3.333 0 118 4.667 3.333 3.333 0 018 11.333zm0-5.333a2 2 0 100 4 2 2 0 000-4z" fill="currentColor" style="color: var(--text-dim)"/>
                </svg>
                <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M2 2l12 12M6.94 6.94A2 2 0 009.06 9.06M8 4C5.333 4 3.04 5.6 2 8c.48 1.08 1.227 2.013 2.153 2.72M13.847 5.28C12.96 4.573 11.52 4 8 4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" style="color: var(--text-dim)"/>
                </svg>
            </button>
        </div>

        <!-- OTP input with Get OTP button -->
        <div v-else-if="type === 'otp'" class="form-field-input form-field-otp" :class="{ 'form-field-input--error': error }">
            <input
                type="text"
                inputmode="numeric"
                :value="value"
                :placeholder="placeholder"
                class="form-field-native"
                @input="$emit('input', $event.target.value)"
            />
            <button type="button" class="form-field-otp-btn" :disabled="otpDisabled" @click="$emit('request-otp')">
                {{ otpLabel }}
            </button>
        </div>

        <!-- Wallet address with Copy button -->
        <div v-else-if="type === 'wallet'" class="form-field-input form-field-wallet">
            <input
                :value="value"
                :placeholder="placeholder"
                class="form-field-native"
                @input="$emit('input', $event.target.value)"
            />
            <button class="form-field-copy" type="button" @click="copy">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" stroke-width="1.5" style="color: var(--text-sub)"/>
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" stroke-width="1.5" style="color: var(--text-sub)"/>
                </svg>
                <span>Copy</span>
            </button>
        </div>

        <!-- Amount with currency prefix -->
        <div v-else-if="type === 'amount'" class="form-field-input form-field-amount">
            <div class="form-field-currency" @click.stop="currencyOpen = !currencyOpen">
                <select :value="currency" class="form-field-currency-select" @change="$emit('update:currency', $event.target.value)">
                    <slot name="currency-options" />
                </select>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
            <div class="form-field-divider"></div>
            <input
                type="number"
                :value="value"
                :placeholder="placeholder || '0.00'"
                class="form-field-native form-field-amount-input"
                @input="$emit('input', $event.target.value)"
            />
        </div>

        <p v-if="error && typeof error === 'string'" class="form-field-hint form-field-hint--taken">{{ error }}</p>
        <p v-else-if="error" class="form-field-hint form-field-hint--taken">{{ label }} {{ $t('common.required') }}</p>
        <p v-else-if="hint" class="form-field-hint" :class="hintType ? `form-field-hint--${hintType}` : ''">{{ hint }}</p>
    </div>
</template>

<script>
export default {
    name: 'FormField',
    model: {
        prop: 'value',
        event: 'input'
    },
    props: {
        label: { type: String, default: '' },
        required: { type: Boolean, default: false },
        type: { type: String, default: 'text' },
        value: { type: [String, Number], default: '' },
        placeholder: { type: String, default: '' },
        options: { type: Array, default: () => [] },
        currency: { type: String, default: '' },
        hint: { type: String, default: '' },
        hintType: { type: String, default: '' },
        error: { type: [String, Boolean], default: false },
        disabled: { type: Boolean, default: false },
        integer: { type: Boolean, default: false },
        otpLabel: { type: String, default: 'Get OTP' },
        otpDisabled: { type: Boolean, default: false }
    },
    data() {
        const now = new Date();
        return {
            dropdownOpen: false,
            currencyOpen: false,
            showPassword: false,
            dateOpen: false,
            pickerView: 'day',
            calYear: now.getFullYear(),
            calMonth: now.getMonth(),
            yearStart: now.getFullYear() - 11,
            weekdays: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
            months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        };
    },
    computed: {
        selectedLabel() {
            const opt = this.options.find(o => o.value === this.value);
            return opt ? opt.label : this.placeholder || 'Select';
        },
        monthLabel() {
            return new Date(this.calYear, this.calMonth, 1)
                .toLocaleString('en', { month: 'long', year: 'numeric' });
        },
        calendarDays() {
            const first = new Date(this.calYear, this.calMonth, 1).getDay();
            const total = new Date(this.calYear, this.calMonth + 1, 0).getDate();
            const days = Array(first).fill(null);
            for (let d = 1; d <= total; d++) days.push(d);
            return days;
        }
    },
    mounted() {
        document.addEventListener('click', this.closeAll);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closeAll);
        this.stopRepeat();
    },
    methods: {
        select(opt) {
            this.$emit('input', opt.value);
            this.dropdownOpen = false;
        },
        onIntegerKeydown(e) {
            if (!this.integer) return;
            if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
        },
        onNativeInput(e) {
            let next = e.target.value;
            if (this.integer) {
                next = (String(next).match(/^\d*/) || [''])[0];
                if (e.target.value !== next) e.target.value = next;
            }
            this.$emit('input', next);
        },
        closeAll() {
            this.dropdownOpen = false;
            this.dateOpen = false;
            this.pickerView = 'day';
        },
        copy() {
            if (this.value) navigator.clipboard.writeText(String(this.value));
        },
        // Runs the action once, then repeats it faster and faster while the button is held.
        startRepeat(action) {
            this.stopRepeat();
            this[action]();
            let delay = 300;
            const tick = () => {
                this[action]();
                delay = Math.max(40, delay * 0.8);
                this.repeatTimer = setTimeout(tick, delay);
            };
            this.repeatTimer = setTimeout(tick, delay);
        },
        stopRepeat() {
            clearTimeout(this.repeatTimer);
            this.repeatTimer = null;
        },
        prevYear() {
            this.calYear--;
        },
        nextYear() {
            this.calYear++;
        },
        prevYearPage() {
            this.yearStart -= 12;
        },
        nextYearPage() {
            this.yearStart += 12;
        },
        openYearView() {
            this.yearStart = this.calYear - 11;
            this.pickerView = 'year';
        },
        prevMonth() {
            if (this.calMonth === 0) { this.calMonth = 11; this.calYear--; }
            else this.calMonth--;
        },
        nextMonth() {
            if (this.calMonth === 11) { this.calMonth = 0; this.calYear++; }
            else this.calMonth++;
        },
        selectDate(day) {
            const mm = String(this.calMonth + 1).padStart(2, '0');
            const dd = String(day).padStart(2, '0');
            this.$emit('input', `${this.calYear}-${mm}-${dd}`);
            this.dateOpen = false;
        },
        isSelected(day) {
            if (!this.value) return false;
            const mm = String(this.calMonth + 1).padStart(2, '0');
            const dd = String(day).padStart(2, '0');
            return this.value === `${this.calYear}-${mm}-${dd}`;
        },
        isToday(day) {
            const now = new Date();
            return now.getFullYear() === this.calYear &&
                now.getMonth() === this.calMonth &&
                now.getDate() === day;
        }
    }
};
</script>

<style lang="scss" scoped>
.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-field-label {
    color: var(--text-sub);
    font-size: 14px;
    font-weight: 500;
    font-family: 'Urbanist', sans-serif;
    line-height: 20px;
}

.form-field-required {
    color: var(--error);
    margin-left: 2px;
}

.form-field-input {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--surface-1);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 10px 12px;
    position: relative;
    cursor: pointer;

    &.form-field-amount {
        padding: 0;
        cursor: default;
    }

    &--error {
        border-color: var(--error);
    }

    &--disabled {
        opacity: 0.6;
        cursor: not-allowed;

        .form-field-native {
            cursor: not-allowed;
        }
    }
}

.form-field-value {
    flex: 1;
    color: var(--text);
    font-size: 14px;
    font-weight: 400;
    font-family: 'Urbanist', sans-serif;
    line-height: 22px;
}

.form-field-native {
    flex: 1;
    background: none;
    border: none;
    outline: none;
    color: var(--text);
    font-size: 14px;
    font-weight: 400;
    font-family: 'Urbanist', sans-serif;
    line-height: 22px;
    cursor: text;

    &::placeholder {
        color: var(--text-dim);
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        appearance: none;
    }
}

.form-field-placeholder {
    color: var(--text-dim) !important;
}

.date-picker {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    width: 280px;
    background: var(--surface-1);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 16px;
    z-index: 100;
    box-shadow: 0 8px 24px color-mix(in srgb, var(--black) 50%, transparent);
    user-select: none;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
}

.date-picker-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}

.date-month-label {
    color: var(--text);
    font-size: 14px;
    font-weight: 600;
    font-family: 'Urbanist', sans-serif;
}

.date-nav-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    border-radius: 6px;

    &:hover {
        background: var(--surface-hover);
    }
}

.date-weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    margin-bottom: 6px;

    span {
        text-align: center;
        color: var(--text-dim);
        font-size: 11px;
        font-weight: 600;
        font-family: 'Urbanist', sans-serif;
        padding: 4px 0;
    }
}

.date-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
}

.date-month-clickable {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 2px;

    &:hover {
        color: var(--text);
    }
}

.date-ym-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}

.date-ym-grid {
    padding: 4px 0;
}

.date-month-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
}

.date-month-cell {
    text-align: center;
    padding: 8px 4px;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'Urbanist', sans-serif;
    color: var(--text-sub);
    cursor: pointer;

    &:hover {
        background: var(--surface-hover);
        color: var(--text);
    }

    &.date-cell--selected {
        background: linear-gradient(180deg, var(--primary) 0%, var(--primary-deep) 100%);
        color: var(--text);
        font-weight: 700;
    }
}

.date-cell {
    text-align: center;
    padding: 6px 0;
    border-radius: 6px;
    font-size: 13px;
    font-family: 'Urbanist', sans-serif;
    color: var(--text-sub);
    cursor: pointer;

    &:hover:not(.date-cell--empty) {
        background: var(--surface-hover);
        color: var(--text);
    }

    &--empty {
        cursor: default;
    }

    &--today {
        color: var(--text);
        font-weight: 700;
        border: 1px solid var(--accent);
    }

    &--selected {
        background: linear-gradient(180deg, var(--primary) 0%, var(--primary-deep) 100%);
        color: var(--text);
        font-weight: 700;
    }
}

.form-field-eye {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.form-field-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: var(--surface-1);
    border: 1px solid var(--border);
    border-radius: 8px;
    z-index: 10;
    max-height: 200px;
    overflow-y: auto;
    scrollbar-width: thin;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }
}

.form-field-option {
    padding: 10px 12px;
    color: var(--text-sub);
    font-size: 14px;
    font-family: 'Urbanist', sans-serif;
    cursor: pointer;

    &:hover {
        background: var(--surface-hover);
    }

    &.selected {
        color: var(--accent);
    }
}

/* Amount */
.form-field-currency {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 12px;
    flex-shrink: 0;
    cursor: pointer;

    svg {
        color: var(--text-muted);
        pointer-events: none;
        flex-shrink: 0;
    }
}

.form-field-currency-select {
    background: transparent;
    border: none;
    outline: none;
    color: var(--text);
    font-size: 14px;
    font-family: 'Urbanist', sans-serif;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;

    option {
        background: var(--surface-1);
        color: var(--text);
    }
}

.form-field-divider {
    width: 1px;
    height: 24px;
    background: var(--border);
    flex-shrink: 0;
}

.form-field-amount-input {
    padding: 10px 12px 10px 10px;
}

.form-field-hint {
    color: var(--text-hint);
    font-size: 12px;
    font-weight: 400;
    font-family: 'Urbanist', sans-serif;
    line-height: 18px;
    margin: 0;
    padding: 0px 6px;

    &--taken { color: var(--error); }
    &--available { color: var(--success-bright); }
}

/* OTP */
.form-field-otp {
    padding: 4px 4px 4px 12px;
    cursor: default;

    .form-field-native {
        padding: 6px 0;
    }
}

.form-field-otp-btn {
    flex-shrink: 0;
    white-space: nowrap;
    padding: 8px 14px;
    border: none;
    border-radius: 6px;
    background: linear-gradient(180deg, var(--primary) 0%, var(--primary-deep) 100%);
    color: var(--text);
    font-size: 13px;
    font-weight: 600;
    font-family: 'Urbanist', sans-serif;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

/* Wallet */
.form-field-wallet {
    padding: 0;
    cursor: default;

    .form-field-native {
        padding: 10px 12px;
    }
}

.form-field-copy {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 14px;
    border: none;
    border-left: 1px solid var(--border);
    border-radius: 0 8px 8px 0;
    background: transparent;
    color: var(--text-sub);
    font-size: 14px;
    font-weight: 600;
    font-family: 'Urbanist', sans-serif;
    cursor: pointer;
    flex-shrink: 0;
    white-space: nowrap;

    &:hover {
        background: color-mix(in srgb, var(--white) 5%, transparent);
    }
}
</style>
